import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSafeId } from 'site-modules/shared/hooks/use-safe-id';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';

export const WIDGET_TYPES = {
  EMBEDDED_FLOW_A: 'embeddedFlowA',
  EMBEDDED_FLOW_B: 'embeddedFlowB',
  ZIP_WIDGET_A: 'zipWidgetA',
  ZIP_WIDGET_B: 'zipWidgetB',
};

const WIDGET_DEFINITION = {
  [WIDGET_TYPES.EMBEDDED_FLOW_A]: {
    adId: 'b46c6962-637b-4177-8ae6-8843ce2dd8df',
    s2: 'leaseNewsletter',
    style: { minHeight: '620px' },
  },
  [WIDGET_TYPES.EMBEDDED_FLOW_B]: {
    adId: 'cea957ed-0d8d-4818-b5e9-60848731508c',
    s2: 'leaseNewsletter',
    style: { minHeight: '620px' },
  },
  [WIDGET_TYPES.ZIP_WIDGET_A]: {
    adId: 'd6b9bb74-3318-4404-8785-28a0ab28f480',
    s2: 'calculatorLP',
    style: { minHeight: '304px' },
  },
  [WIDGET_TYPES.ZIP_WIDGET_B]: {
    adId: '3bd5e1c9-1ffc-4e2d-9d98-1165d50e993e',
    s2: 'calculatorLP',
    style: { height: '622px' },
  },
};

const HOST_PATH = 'https://static.myfinance.com/embed';
const DATA_S1_MAP = {
  true: 'brLandingPage', // for landing page
  false: 'leadThankYouPage', // for widget
};
const TRACKING_DATA = {
  action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
  action_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
  action_name: TrackingConstant.ACTION_WIDGET_VIEW,
  action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
};

const HOST_REFERRER = 'https://www.edmunds.com';

export function WidgetContent({
  widgetType = WIDGET_TYPES.EMBEDDED_FLOW_A,
  widgetStyles = null,
  widgetClassName = null,
  isLandingPage = false,
  location = {},
  zipCode = null,
}) {
  const widgetRef = useRef();
  const safeId = useSafeId();
  const [isLoading, setLoading] = useState(true);

  const frameId = `myfi-widget-id-${safeId}`;
  const dataS1 = isLandingPage ? DATA_S1_MAP.true : DATA_S1_MAP.false;
  const dataS2 = location?.query?.subID2 ?? WIDGET_DEFINITION[widgetType].s2;

  useEffect(() => {
    const observer = new MutationObserver(() => {
      observer.disconnect();
      const iframe = widgetRef.current.querySelector('iframe');
      function onLoad() {
        iframe.removeEventListener('load', onLoad);
        widgetRef.current.classList.toggle('myFinance-widget', false);
        setLoading(false);
      }
      iframe.addEventListener('load', onLoad);
    });
    observer.observe(widgetRef.current, { childList: true });

    if (window.initializeMyFinance) {
      window.initializeMyFinance(true);
    } else {
      const cohesionScript = document.createElement('script');
      cohesionScript.text =
        "!function(co,h,e,s,i,o,n){var d='documentElement';var a='className';h[d][a]+=' preampjs fusejs'; n.k=e;co._Cohesion=n;co._Preamp={k:s,start:new Date};co._Fuse={k:i};co._Tagular={k:o}; [e,s,i,o].map(function(x){co[x]=co[x]||function(){(co[x].q=co[x].q||[]).push([].slice.call(arguments))}}); h.addEventListener('DOMContentLoaded',function(){co.setTimeout(function(){ var u=h[d][a];h[d][a]=u.replace(/ ?preampjs| ?fusejs/g,'')},3e3); co._Preamp.docReady=co._Fuse.docReady=!0});var z=h.createElement('script'); z.async=1;z.src='https://cdn.cohesionapps.com/cohesion/cohesion-latest.min.js';h.head.appendChild(z);} (window, document, 'cohesion', 'preamp', 'fuse', 'tagular', { consent: { onetrust: { enabled: true, optIn: false, }, required: true, }, tagular: { apiKey: '', writeKey: 'wk_2M3TlVcWbz8i1RTZjLkSmbWfyQC', sourceKey: 'src_2M3TlcG1z0ZFw3v1MHxyIkkKO5d', apiHost: 'https://ingest.make.rvapps.io/v2/t', }, });";
      document.body.appendChild(cohesionScript);

      window?.cohesion?.('ready', () => {
        const myFinanceScript = document.createElement('script');
        myFinanceScript.src = `${HOST_PATH}/myFinance.js`;
        myFinanceScript.async = !0;
        document.body.appendChild(myFinanceScript);
      });
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        ...TRACKING_DATA,
        creative_id: 'partner_insurance_widget',
        value: `doc_referrer_url=${HOST_REFERRER}${location?.pathname ?? ''}`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames('myFinance-widget pos-r', widgetClassName)}
      style={{ ...WIDGET_DEFINITION[widgetType].style, ...widgetStyles }}
      data-zip={zipCode}
      data-s1={dataS1}
      data-s2={dataS2}
      data-ad-id={WIDGET_DEFINITION[widgetType].adId}
      data-campaign="edmunds-coverage-embedded-lead-form"
      id={frameId}
      ref={widgetRef}
    >
      {isLoading && <LoadingSpinner classes="pos-a top-0 left-0 w-100 h-100" isLoading />}
    </div>
  );
}

WidgetContent.propTypes = {
  widgetStyles: PropTypes.shape({}),
  widgetClassName: PropTypes.string,
  isLandingPage: PropTypes.bool,
  location: PropTypes.shape({}),
  zipCode: PropTypes.string,
  widgetType: PropTypes.string,
};
